/**
 * Application constants and configuration
 */

/**
 * API Configuration
 */
export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_URL || "http://localhost:3000/api",
  TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
} as const;

/**
 * Application Routes
 */
export const ROUTES = {
  HOME: "/",
  OFFER_DETAIL: "/offer/:id",
  CREATE_OFFER: "/create",
  PROFILE: "/profile",
  FAVORITES: "/favorites",
  SEARCH: "/search",
  ADMIN: "/admin",
  ADMIN_USERS: "/admin/users",
  ONBOARDING: "/onboarding",
  NOT_FOUND: "/404",
} as const;

/**
 * Offer Categories
 */
export const OFFER_CATEGORIES = [
  "Audio Transformers",
  "Power Transformers",
  "Isolation Transformers",
  "Auto Transformers",
  "Toroidal Transformers",
  "Vacuum Tube Amplifiers",
  "Power Supplies",
  "Audio Equipment",
  "Vintage Electronics",
  "Test Equipment",
  "Components",
  "Cables & Connectors",
  "Tools",
  "Books & Manuals",
  "Other",
] as const;

export type OfferCategory = (typeof OFFER_CATEGORIES)[number];

/**
 * Offer Conditions
 */
export const OFFER_CONDITIONS = [
  { value: "new", label: "New" },
  { value: "used_excellent", label: "Used - Excellent" },
  { value: "used_good", label: "Used - Good" },
  { value: "used_fair", label: "Used - Fair" },
  { value: "refurbished", label: "Refurbished" },
  { value: "for_parts", label: "For Parts/Repair" },
] as const;

export type OfferCondition = (typeof OFFER_CONDITIONS)[number]["value"];

/**
 * Offer Types
 */
export const OFFER_TYPES = {
  BUY: "BUY",
  SELL: "SELL",
} as const;

export type OfferType = (typeof OFFER_TYPES)[keyof typeof OFFER_TYPES];

/**
 * Offer Status
 */
export const OFFER_STATUS = {
  ACTIVE: "active",
  SOLD: "sold",
  EXPIRED: "expired",
  DRAFT: "draft",
  PENDING: "pending",
  REJECTED: "rejected",
} as const;

export type OfferStatus = (typeof OFFER_STATUS)[keyof typeof OFFER_STATUS];

/**
 * User Roles
 */
export const USER_ROLES = {
  USER: "user",
  ADMIN: "admin",
  MODERATOR: "moderator",
} as const;

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

export const ALL_GEO_LOCATIONS = [
  "USA",
  "Canada",
  "UK",
  "Germany",
  "France",
  "Australia",
  "Japan",
  "Brazil",
  "India",
  "China",
  "Russia",
  "Italy",
  "Spain",
  "Mexico",
  "Indonesia",
  "Netherlands",
  "Saudi Arabia",
  "Switzerland",
  "Turkey",
  "Sweden",
  "Poland",
  "Belgium",
  "Norway",
  "Austria",
  "Denmark",
  "Ireland",
  "Singapore",
  "Malaysia",
  "Thailand",
  "Vietnam",
  "Philippines",
  "South Korea",
  "New Zealand",
  "South Africa",
  "Nigeria",
  "Egypt",
  "Argentina",
  "Chile",
  "Colombia",
  "Peru",
  "Venezuela",
  "Ukraine",
  "Romania",
  "Greece",
  "Portugal",
  "Hungary",
  "Czech Republic",
  "Finland",
  "Israel",
  "United Arab Emirates",
  "Qatar",
  "Kuwait",
  "Hong Kong",
  "Taiwan",
];

export const TRAFFIC_SOURCES = [
  "Facebook",
  "SEO",
  "ASO",
  "PPC",
  "TikTok",
  "Instagram",
  "YouTube",
  "Telegram",
  "Push notifications",
  "Teaser networks",
  "Popunder/Clickunder",
  "Email marketing",
  "SMS marketing",
];
export const PAYMENT_MODELS = ["CPA", "RevShare", "Hybrid"];

/**
 * File Upload Limits
 */
export const FILE_UPLOAD = {
  MAX_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_TYPES: ["image/jpeg", "image/png", "image/gif", "image/webp"],
  MAX_FILES: 5,
} as const;

/**
 * Pagination
 */
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  LOAD_MORE_SIZE: 10,
} as const;

/**
 * Search Configuration
 */
export const SEARCH_CONFIG = {
  MIN_QUERY_LENGTH: 2,
  MAX_QUERY_LENGTH: 100,
  DEBOUNCE_DELAY: 300, // milliseconds
  MAX_RESULTS: 50,
  DEFAULT_RADIUS: 50, // kilometers
} as const;

/**
 * Telegram WebApp Configuration
 */
export const TELEGRAM_CONFIG = {
  BOT_USERNAME: import.meta.env.VITE_TELEGRAM_BOT_USERNAME || "TraformaticBot",
  WEBAPP_URL: import.meta.env.VITE_WEBAPP_URL || "https://trafomatic.app",
  SUPPORT_CHAT: "@TraformaticSupport",
} as const;

/**
 * Theme Configuration
 */
export const THEME_CONFIG = {
  DEFAULT_THEME: "dark",
  STORAGE_KEY: "trafomatic-theme",
  TRANSITION_DURATION: 150, // milliseconds
} as const;

/**
 * Animation Durations (in milliseconds)
 */
export const ANIMATIONS = {
  FAST: 150,
  NORMAL: 300,
  SLOW: 500,
  VERY_SLOW: 1000,
} as const;

/**
 * Breakpoints (matching Tailwind CSS)
 */
export const BREAKPOINTS = {
  SM: 640,
  MD: 768,
  LG: 1024,
  XL: 1280,
  "2XL": 1536,
} as const;

/**
 * Local Storage Keys
 */
export const STORAGE_KEYS = {
  USER_PREFERENCES: "trafomatic-preferences",
  SEARCH_HISTORY: "trafomatic-search-history",
  FAVORITES: "trafomatic-favorites",
  LAST_LOCATION: "trafomatic-last-location",
  ONBOARDING_COMPLETED: "trafomatic-onboarding-completed",
  DRAFT_OFFER: "trafomatic-draft-offer",
} as const;

/**
 * Key used to persist user's selected onboarding role between pages during auth flow
 */
export const ONBOARDING_ROLE_KEY = "onboardingRole";

/**
 * Error Messages
 */
export const ERROR_MESSAGES = {
  NETWORK_ERROR: "Network error. Please check your connection.",
  UNAUTHORIZED: "You are not authorized to perform this action.",
  FORBIDDEN: "Access denied.",
  NOT_FOUND: "The requested resource was not found.",
  VALIDATION_ERROR: "Please check your input and try again.",
  UNKNOWN_ERROR: "An unexpected error occurred.",
  TELEGRAM_NOT_AVAILABLE: "This feature is only available in Telegram.",
  LOCATION_NOT_AVAILABLE: "Location services are not available.",
  CAMERA_NOT_AVAILABLE: "Camera access is not available.",
  FILE_TOO_LARGE: "File size is too large. Maximum size is 5MB.",
  INVALID_FILE_TYPE: "Invalid file type. Please select an image file.",
} as const;

/**
 * Success Messages
 */
export const SUCCESS_MESSAGES = {
  OFFER_CREATED: "Your offer has been created successfully!",
  OFFER_UPDATED: "Your offer has been updated successfully!",
  OFFER_DELETED: "Your offer has been deleted successfully!",
  PROFILE_UPDATED: "Your profile has been updated successfully!",
  SETTINGS_SAVED: "Your settings have been saved successfully!",
  MESSAGE_SENT: "Your message has been sent successfully!",
  FAVORITE_ADDED: "Added to your favorites!",
  FAVORITE_REMOVED: "Removed from your favorites!",
} as const;

/**
 * Default Values
 */
export const DEFAULTS = {
  CURRENCY: "USD",
  LANGUAGE: "en",
  LOCATION: "Unknown",
  PAGE_TITLE: "Trafomatic - Transformer Marketplace",
  META_DESCRIPTION:
    "Buy and sell audio transformers, power equipment, and vintage electronics on Trafomatic marketplace.",
  OFFER_EXPIRY_DAYS: 30,
} as const;

/**
 * Regex Patterns
 */
export const PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^\+?[1-9]\d{0,15}$/,
  USERNAME: /^[a-zA-Z0-9_]{3,20}$/,
  TELEGRAM_USERNAME: /^@?[a-zA-Z0-9_]{5,32}$/,
  URL: /^https?:\/\/.+/,
  PRICE: /^\d+(\.\d{1,2})?$/,
  POSTAL_CODE: /^[a-zA-Z0-9\s-]{3,10}$/,
} as const;

/**
 * Feature Flags
 */
export const FEATURES = {
  ENABLE_GEOLOCATION: true,
  ENABLE_PUSH_NOTIFICATIONS: true,
  ENABLE_DARK_MODE: true,
  ENABLE_OFFLINE_MODE: false,
  ENABLE_ANALYTICS: false,
  ENABLE_A_B_TESTING: false,
  ENABLE_CHAT: true,
  ENABLE_REVIEWS: false,
  ENABLE_PAYMENTS: false,
} as const;

/**
 * Social Media Links
 */
export const SOCIAL_LINKS = {
  TELEGRAM: "https://t.me/TraformaticChannel",
  WEBSITE: "https://trafomatic.com",
  GITHUB: "https://github.com/trafomatic",
  SUPPORT_EMAIL: "support@trafomatic.com",
} as const;

/**
 * Development Configuration
 */
export const DEV_CONFIG = {
  ENABLE_LOGGER: import.meta.env.DEV,
  ENABLE_REDUX_DEVTOOLS: import.meta.env.DEV,
  MOCK_API_DELAY: 1000, // milliseconds
  SHOW_COMPONENT_BORDERS: false,
} as const;

export default {
  API_CONFIG,
  ROUTES,
  OFFER_CATEGORIES,
  OFFER_CONDITIONS,
  OFFER_TYPES,
  OFFER_STATUS,
  USER_ROLES,
  ALL_GEO_LOCATIONS,
  TRAFFIC_SOURCES,
  FILE_UPLOAD,
  PAGINATION,
  SEARCH_CONFIG,
  TELEGRAM_CONFIG,
  THEME_CONFIG,
  ANIMATIONS,
  BREAKPOINTS,
  STORAGE_KEYS,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  DEFAULTS,
  PATTERNS,
  FEATURES,
  SOCIAL_LINKS,
  DEV_CONFIG,
};
